import { formatCentsAsUsd } from "../lib/format-menu";
import type { AchAuthorizationPreview } from "../lib/fetch-ach-authorization-preview";

/**
 * **Nacha WEB consent screen.**
 *
 * The consumer must be shown the amount, the account, the creditor, the
 * timing, and how to revoke — and must affirmatively assent — *before* any ACH
 * debit is authorized. This component is that disclosure.
 *
 * Two rules it exists to enforce:
 *
 * 1. **The legal text is the server's**, rendered verbatim from
 *    `preview.authorizationText`. The summary table above it is a readability
 *    aid, not the authorization. If the two ever disagreed, the stored record
 *    would not be what the consumer read, which is the one thing the record
 *    has to guarantee.
 * 2. **Unchecked by default, and submit stays disabled until checked.** Assent
 *    has to be an act, not the absence of one.
 */
export function AchAuthorizationConsent({
  preview,
  accepted,
  onAcceptedChange,
  loading,
  error,
  onRetry,
  disabled = false,
}: {
  preview: AchAuthorizationPreview | null;
  accepted: boolean;
  onAcceptedChange: (next: boolean) => void;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  disabled?: boolean;
}) {
  if (loading) {
    return (
      <section className="rounded-lg border border-neutral-300 bg-white p-4">
        <h3 className="text-sm font-semibold">Authorize your bank payment</h3>
        <p className="mt-2 text-sm text-neutral-600">Loading payment authorization…</p>
      </section>
    );
  }

  if (error != null || preview == null) {
    return (
      <section className="rounded-lg border border-red-300 bg-red-50 p-4">
        <h3 className="text-sm font-semibold text-red-900">
          Payment authorization unavailable
        </h3>
        <p className="mt-2 text-sm text-red-900">
          {error === "customer_bank_not_linked"
            ? "Link a bank account to pay by bank, or choose to pay the driver in cash."
            : "We could not load your payment authorization. You can retry, or pay the driver in cash."}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-md border border-red-400 px-3 py-1.5 text-sm font-medium text-red-900"
        >
          Retry
        </button>
      </section>
    );
  }

  const multi = preview.legs.length > 1;

  return (
    <section className="rounded-lg border border-neutral-300 bg-white p-4">
      <h3 className="text-sm font-semibold">Authorize your bank payment</h3>

      <dl className="mt-3 space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-neutral-600">Pay from</dt>
          <dd className="text-right font-medium">
            {preview.bankName ?? "Bank account"} ending in {preview.bankAccountLast4}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-neutral-600">When</dt>
          <dd className="text-right font-medium">{preview.timingDescription}</dd>
        </div>
      </dl>

      {/* Every creditor and every amount, itemized. With one leg this reads as
          a single payee; with two it makes the split explicit rather than
          surprising the consumer with two entries on their statement. */}
      <table className="mt-3 w-full text-sm">
        <tbody>
          {preview.legs.map((leg) => (
            <tr key={leg.legPurpose}>
              <td className="py-1 pr-2 text-neutral-600">
                {leg.creditorLegalName}
                {leg.creditorLicenseNumber != null ? (
                  <span className="block text-xs text-neutral-500">
                    License {leg.creditorLicenseNumber}
                  </span>
                ) : null}
              </td>
              <td className="py-1 text-right font-medium tabular-nums">
                {formatCentsAsUsd(leg.amountCents)}
              </td>
            </tr>
          ))}
          <tr className="border-t border-neutral-200">
            <td className="py-1 pr-2 font-semibold">Total</td>
            <td className="py-1 text-right font-semibold tabular-nums">
              {formatCentsAsUsd(preview.totalAmountCents)}
            </td>
          </tr>
        </tbody>
      </table>

      {multi ? (
        <p className="mt-2 text-xs text-neutral-600">
          You will see {preview.legs.length} separate debits on your bank statement for this
          order. Together they equal the total above.
        </p>
      ) : null}

      {/* The authorization itself. Server-rendered, displayed verbatim,
          preserving its own line breaks. */}
      <label className="mt-4 flex items-start gap-2">
        <input
          type="checkbox"
          checked={accepted}
          disabled={disabled}
          onChange={(e) => onAcceptedChange(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0"
          data-testid="ach-authorization-checkbox"
        />
        <span className="sr-only">I authorize this bank payment</span>
        <span
          className="whitespace-pre-wrap text-xs leading-relaxed text-neutral-800"
          data-testid="ach-authorization-text"
        >
          {preview.authorizationText}
        </span>
      </label>
    </section>
  );
}
